import { useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  Edit,
  RestartAlt,
  Refresh,
  DragIndicator,
  HelpOutline,
} from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { isAdminOrManager } from '../../utils/roles'
import { userAtom } from '../../state/auth'
import {
  northStarTemplatesApi,
  NorthStarSectionTemplate,
  NorthStarRenderType,
} from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import TemplateFieldsEditor from '../../components/north-star/TemplateFieldsEditor'
import HelpDrawer from '../../components/HelpDrawer'

// Coach-friendly labels + 1-sentence descriptions for each render type.
// The internal slug stays the same on the backend; this just controls UI text.
const RENDER_TYPE_META: Record<
  NorthStarRenderType,
  { label: string; description: string }
> = {
  hero: {
    label: 'Hero / Cover',
    description:
      'Big introduction at the top of the page (client name, role, tagline). Use this once, as the first section.',
  },
  narrative: {
    label: 'Narrative',
    description:
      'A long-form quote with supporting evidence cards. Best for setting the strategic frame.',
  },
  comparison: {
    label: 'Side-by-side Comparison',
    description:
      'A row of cards comparing items — e.g., 3 career paths with fit scores.',
  },
  deepDive: {
    label: 'Detailed Analysis with Chart',
    description:
      'One block per item with a 5-axis radar chart showing dimensions.',
  },
  chartCompare: {
    label: 'Compensation Comparison',
    description:
      'Big-number salary cards plus a bar chart comparing them.',
  },
  chartTrajectory: {
    label: 'Earnings Trajectory',
    description: '10-year line chart projecting earnings over time, with per-item narratives.',
  },
  tagGrid: {
    label: 'Tag Grid',
    description:
      'Grouped lists of items — e.g., wildcard companies organized by path.',
  },
  decision: {
    label: 'Decision Card',
    description:
      'Highlighted recommendation with primary, secondary, and avoid options.',
  },
  actionCards: {
    label: 'Action Cards',
    description: 'A 2×2 grid of card-style action items the client should take.',
  },
}

const RENDER_TYPES = Object.keys(RENDER_TYPE_META) as NorthStarRenderType[]

const renderTypeLabel = (rt: NorthStarRenderType) =>
  RENDER_TYPE_META[rt]?.label || rt

const emptyTemplate = (): NorthStarSectionTemplate => ({
  sectionTypeId: '',
  name: '',
  description: '',
  renderType: 'narrative',
  fields: [],
  aiPromptSnippet: '',
  defaultOrder: 99,
  defaultEnabled: true,
  isActive: true,
  version: 0,
})

// --------------------------- Sortable Row ---------------------------

interface SortableRowProps {
  template: NorthStarSectionTemplate
  isAdmin: boolean
  onEdit: (t: NorthStarSectionTemplate) => void
  onDeactivate: (t: NorthStarSectionTemplate) => void
}

function SortableRow({ template: t, isAdmin, onEdit, onDeactivate }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: t.sectionTypeId,
    disabled: !isAdmin,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : t.isActive ? 1 : 0.55,
    boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.12)' : undefined,
    cursor: isAdmin ? (isDragging ? 'grabbing' : 'default') : 'default',
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Tooltip title={isAdmin ? 'Drag to reorder' : 'Reordering requires admin role'}>
              <Box
                {...attributes}
                {...listeners}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isAdmin ? 'text.secondary' : 'action.disabled',
                  cursor: isAdmin ? 'grab' : 'not-allowed',
                  px: 0.5,
                  '&:active': { cursor: isAdmin ? 'grabbing' : 'not-allowed' },
                }}
              >
                <DragIndicator />
              </Box>
            </Tooltip>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t.name}
                </Typography>
                <Chip
                  label={renderTypeLabel(t.renderType)}
                  size="small"
                  variant="outlined"
                />
                {!t.defaultEnabled && (
                  <Chip label="opt-in only" size="small" color="warning" variant="outlined" />
                )}
                {!t.isActive && <Chip label="inactive" size="small" color="default" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t.sectionTypeId} · {(t.fields || []).length} field
                {(t.fields || []).length === 1 ? '' : 's'}
              </Typography>
              {t.description && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t.description}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.5}>
              <IconButton onClick={() => onEdit(t)} disabled={!isAdmin} aria-label="Edit">
                <Edit />
              </IconButton>
              {t.isActive && (
                <Button
                  size="small"
                  color="warning"
                  onClick={() => onDeactivate(t)}
                  disabled={!isAdmin}
                >
                  Deactivate
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </div>
  )
}

// --------------------------- Main page ---------------------------

export default function NorthStarTemplates() {
  const [user] = useAtom(userAtom)
  const { showSuccess, showError } = useNotification()
  const [templates, setTemplates] = useState<NorthStarSectionTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<NorthStarSectionTemplate>(emptyTemplate())
  const [isNew, setIsNew] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  const isAdmin = user?.role === 'admin'
  const isPrivileged = isAdminOrManager(user)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  useEffect(() => {
    if (!isPrivileged) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged])

  const load = async () => {
    try {
      setLoading(true)
      const list = await northStarTemplatesApi.list()
      setTemplates(list)
    } catch (err) {
      console.error(err)
      showError('Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (t: NorthStarSectionTemplate) => {
    setDraft({ ...t })
    setIsNew(false)
    setDialogOpen(true)
  }

  const handleNew = () => {
    setDraft({ ...emptyTemplate(), defaultOrder: templates.length })
    setIsNew(true)
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(draft.sectionTypeId)) {
      showError('sectionTypeId must be lowercase, kebab-case, 2–64 chars')
      return
    }
    if (!draft.name.trim()) {
      showError('Name is required')
      return
    }
    try {
      setSaving(true)
      const saved = await northStarTemplatesApi.save(draft)
      setDialogOpen(false)
      await load()
      showSuccess(`Saved "${saved.name}"`)
    } catch (err) {
      console.error(err)
      showError('Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleSeed = async () => {
    if (!window.confirm('Seed missing default templates? Existing ones will be left alone.')) return
    try {
      setSaving(true)
      const result = await northStarTemplatesApi.seed(false)
      await load()
      showSuccess(`Seeded: ${result.created.length} new, ${result.skipped.length} kept`)
    } catch (err) {
      console.error(err)
      showError('Seed failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (t: NorthStarSectionTemplate) => {
    if (
      !window.confirm(
        `Deactivate "${t.name}"? Coaches will no longer see this section in new drafts.`
      )
    )
      return
    try {
      setSaving(true)
      await northStarTemplatesApi.deactivate(t.sectionTypeId)
      await load()
      showSuccess(`Deactivated`)
    } catch (err) {
      console.error(err)
      showError('Deactivate failed')
    } finally {
      setSaving(false)
    }
  }

  // Drag-end: optimistically reorder, persist new defaultOrder values per template.
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    if (!isAdmin) return

    const sorted = [...templates].sort(
      (a, b) => (a.defaultOrder ?? 0) - (b.defaultOrder ?? 0)
    )
    const oldIndex = sorted.findIndex((t) => t.sectionTypeId === active.id)
    const newIndex = sorted.findIndex((t) => t.sectionTypeId === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    const reordered = arrayMove(sorted, oldIndex, newIndex)
    // Reassign defaultOrder to match new array indexes
    const withNewOrders = reordered.map((t, i) => ({ ...t, defaultOrder: i }))
    setTemplates(withNewOrders) // optimistic UI

    // Persist only templates whose order actually changed
    try {
      setSaving(true)
      const changed = withNewOrders.filter(
        (t) =>
          t.defaultOrder !==
          templates.find((orig) => orig.sectionTypeId === t.sectionTypeId)?.defaultOrder
      )
      await Promise.all(
        changed.map((t) =>
          northStarTemplatesApi.save({
            ...t,
            // Save() always bumps version. We pass the full template; backend will keep history.
          })
        )
      )
      showSuccess('Order saved')
      // Reload to get any version/timestamp updates
      await load()
    } catch (err) {
      console.error(err)
      showError('Failed to save new order — reloading')
      await load()
    } finally {
      setSaving(false)
    }
  }

  if (!isPrivileged) {
    return (
      <Box p={3}>
        <Alert severity="error">Admins only.</Alert>
      </Box>
    )
  }

  const sortedTemplates = [...templates].sort(
    (a, b) => (a.defaultOrder ?? 0) - (b.defaultOrder ?? 0)
  )
  const sortableIds = sortedTemplates.map((t) => t.sectionTypeId)

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4">North Star Templates</Typography>
            <Tooltip title="How does this page work?">
              <IconButton
                onClick={() => setHelpOpen(true)}
                size="small"
                aria-label="Open help drawer"
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Define the section types that compose a client's North Star microsite. Drag to reorder.
            Click a section to edit its fields and AI prompt.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Refresh />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button startIcon={<RestartAlt />} onClick={handleSeed} disabled={!isAdmin || saving}>
            Seed defaults
          </Button>
          <Button startIcon={<Add />} variant="contained" onClick={handleNew} disabled={!isAdmin}>
            New section type
          </Button>
        </Stack>
      </Stack>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Managers can view templates. Only admins can create, edit, or reorder.
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="warning">
          No section templates yet. Click "Seed defaults" to install the 9 starter templates.
        </Alert>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <Stack spacing={1.5}>
              {sortedTemplates.map((t) => (
                <SortableRow
                  key={t.sectionTypeId}
                  template={t}
                  isAdmin={isAdmin}
                  onEdit={handleEdit}
                  onDeactivate={handleDeactivate}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isNew ? 'New section template' : `Edit "${draft.name}"`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Section Type ID"
                value={draft.sectionTypeId}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    sectionTypeId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                  })
                }
                disabled={!isNew}
                sx={{ flex: 1 }}
                size="small"
                required
                helperText="kebab-case; permanent"
              />
              <TextField
                label="Display name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                sx={{ flex: 2 }}
                size="small"
                required
              />
            </Stack>
            <TextField
              label="Description"
              value={draft.description || ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              size="small"
              multiline
              minRows={1}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
                <InputLabel>Render style</InputLabel>
                <Select
                  label="Render style"
                  value={draft.renderType}
                  onChange={(e) =>
                    setDraft({ ...draft, renderType: e.target.value as NorthStarRenderType })
                  }
                  renderValue={(value) => renderTypeLabel(value as NorthStarRenderType)}
                >
                  {RENDER_TYPES.map((rt) => (
                    <MenuItem key={rt} value={rt}>
                      <ListItemText
                        primary={RENDER_TYPE_META[rt].label}
                        secondary={RENDER_TYPE_META[rt].description}
                        secondaryTypographyProps={{
                          sx: { whiteSpace: 'normal', fontSize: 12, mt: 0.25 },
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, pl: 1.5 }}>
                  {RENDER_TYPE_META[draft.renderType]?.description}
                </Typography>
              </FormControl>
              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!draft.defaultEnabled}
                      onChange={(e) => setDraft({ ...draft, defaultEnabled: e.target.checked })}
                    />
                  }
                  label="On by default"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!draft.isActive}
                      onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Stack>
            </Stack>
            <TextField
              label="AI prompt snippet"
              value={draft.aiPromptSnippet || ''}
              onChange={(e) => setDraft({ ...draft, aiPromptSnippet: e.target.value })}
              multiline
              minRows={3}
              helperText="Instruction the AI sees when populating this section. Be specific — generic prompts produce generic output."
            />
            <Divider />
            <Typography variant="overline" color="text.secondary">
              Fields
            </Typography>
            <TemplateFieldsEditor
              fields={draft.fields || []}
              onChange={(next) => setDraft({ ...draft, fields: next })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !isAdmin}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <HelpDrawer
        articleId="admin-north-star-templates"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </Box>
  )
}
